import { useEffect, useRef, useState } from "react";
import { api, apiBase } from "./api";
import { getDeviceId } from "./storage";
import type {
  LatLng,
  LocationFix,
  RoadReport,
  Route,
  TrafficEstimate,
} from "./types";

/** Handles UTF-8 chunks and CRLF framing; never assumes one network chunk is an event. */
export class EventFrames {
  private buffer = "";
  push(chunk: string): { event: string; data: string }[] {
    this.buffer += chunk;
    if (this.buffer.length > 1_000_000)
      throw new Error("עדכון הדיווחים גדול מדי");
    const events: { event: string; data: string }[] = [];
    let match: RegExpExecArray | null;
    while ((match = /\r?\n\r?\n/.exec(this.buffer))) {
      const block = this.buffer.slice(0, match.index);
      this.buffer = this.buffer.slice(match.index + match[0].length);
      let event = "message";
      const data: string[] = [];
      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        if (line.startsWith("data:"))
          data.push(line.slice(5).replace(/^ /, ""));
      }
      if (data.length) events.push({ event, data: data.join("\n") });
    }
    return events;
  }
}

export function locationFix(position: GeolocationPosition): LocationFix {
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy: position.coords.accuracy,
    observedAt: position.timestamp,
    speedMps: position.coords.speed,
    heading: position.coords.heading,
  };
}

export function freshFix(
  fix: LocationFix | null,
  now = Date.now(),
  maxAccuracy = 100,
): fix is LocationFix {
  return (
    !!fix &&
    [fix.lat, fix.lng, fix.accuracy, fix.observedAt].every(Number.isFinite) &&
    Math.abs(fix.lat) <= 90 &&
    Math.abs(fix.lng) <= 180 &&
    fix.accuracy >= 0 &&
    fix.accuracy <= maxAccuracy &&
    now - fix.observedAt <= 30_000 &&
    now - fix.observedAt >= -5000
  );
}

export function useCommunity(center: LatLng, refreshVersion: number) {
  const [reports, setReports] = useState<RoadReport[]>([]);
  const [state, setState] = useState<"connecting" | "live" | "polling" | "retrying">(
    "connecting",
  );
  const [error, setError] = useState("");
  // A 15 km subscription is renewed after roughly 2 km, not on each GPS fix.
  const lat = Math.round(center.lat * 50) / 50,
    lng = Math.round(center.lng * 50) / 50;
  useEffect(() => {
    let disposed = false,
      retries = 0,
      epoch = 0,
      timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | null = null;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      epoch++;
      clearTimeout(timer);
      clearTimeout(watchdog);
      controller?.abort();
    };
    const connect = async () => {
      if (disposed || document.hidden || navigator.onLine === false) return;
      const run = ++epoch;
      const active = () => !disposed && run === epoch;
      const connection = new AbortController();
      controller = connection;
      const startedAt = Date.now();
      setState(current => current === "polling" ? current : retries ? "retrying" : "connecting");
      const arm = () => {
        clearTimeout(watchdog);
        watchdog = setTimeout(() => connection.abort(), 40_000);
      };
      try {
        arm();
        const params = new URLSearchParams({
          lat: String(lat),
          lng: String(lng),
          radius: "15000",
        });
        const response = await fetch(
          `${apiBase}/api/reports/stream?${params}`,
          {
            headers: {
              Accept: "text/event-stream",
              "X-Device-Id": getDeviceId(),
            },
            cache: "no-store",
            signal: connection.signal,
          },
        );
        if (
          !response.ok ||
          !response.headers
            .get("Content-Type")
            ?.includes("text/event-stream") ||
          !response.body
        )
          throw new Error("stream unavailable");
        const reader = response.body.getReader(),
          decoder = new TextDecoder(),
          frames = new EventFrames();
        try {
          while (active()) {
            const { value, done } = await reader.read();
            if (done || !active()) break;
            arm();
            for (const event of frames.push(
              decoder.decode(value, { stream: true }),
            )) {
              if (event.event !== "snapshot") continue;
              const snapshot = JSON.parse(event.data) as {
                reports: RoadReport[];
              };
              if (!Array.isArray(snapshot.reports))
                throw new Error("invalid snapshot");
              if (active()) {
                setReports(snapshot.reports);
                setState("live");
                setError("");
                if (Date.now() - startedAt >= 30_000) retries = 0;
              }
            }
          }
        } finally {
          // A disconnected transport must not hold reconnection behind a pending cancel.
          void reader.cancel().catch(() => {});
          reader.releaseLock();
        }
      } catch {
        /* A full snapshot after reconnect repairs missed events. */
      } finally {
        connection.abort();
        if (active()) clearTimeout(watchdog);
      }
      if (!active()) return;
      const fallback = new AbortController();
      controller = fallback;
      try {
        const current = await api.reports({ lat, lng }, fallback.signal);
        if (active()) {
          setReports(current);
          setState("polling");
          setError("");
        }
      } catch {
        if (active()) {
          setState("retrying");
          setError(!Boolean(navigator.onLine)
            ? "אין חיבור לאינטרנט. הדיווחים יתעדכנו כשהחיבור יחזור."
            : "עדכון הדיווחים אינו זמין כרגע. מנסים שוב אוטומטית.");
        }
      }
      if (!active()) return;
      timer = setTimeout(
        connect,
        Math.min(30_000, 5000 * 2 ** Math.min(retries++, 3)),
      );
    };
    const resume = () => {
      stop();
      if (!document.hidden && navigator.onLine !== false) {
        retries = 0;
        setError("");
        void connect();
      }
    };
    const offline = () => {
      stop();
      setState("retrying");
      setError("אין חיבור לאינטרנט. הדיווחים יתעדכנו כשהחיבור יחזור.");
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);
    window.addEventListener("offline", offline);
    setError("");
    if (navigator.onLine === false) offline();
    else void connect();
    return () => {
      disposed = true;
      stop();
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
      window.removeEventListener("offline", offline);
    };
  }, [lat, lng, refreshVersion]);
  return { reports, setReports, state, error };
}

export function useTraffic(
  route: Route | null,
  active: boolean,
  fix: LocationFix | null,
  share: boolean,
  arrived = false,
) {
  const [estimate, setEstimate] = useState<TrafficEstimate | null>(null);
  const [error, setError] = useState("");
  const latest = useRef(fix);
  const arrival = useRef(arrived);
  const lastAttempt = useRef({ at: Number.NEGATIVE_INFINITY, observedAt: Number.NEGATIVE_INFINITY });
  const notifyArrival = useRef<(() => void) | null>(null);
  const flushTerminal = useRef<((point: LocationFix) => Promise<void>) | null>(null);
  latest.current = fix;
  arrival.current = arrived;
  useEffect(() => {
    setEstimate(null);
    setError("");
    if (!route || !active) return;
    const controller = new AbortController();
    let disposed = false,
      estimateBusy = false,
      sampleBusy = false,
      terminalQueued = false,
      terminalDone = false;
    let terminalFix: LocationFix | null = null;
    let terminalTimer: ReturnType<typeof setTimeout> | undefined;
    let terminalDeadline: ReturnType<typeof setTimeout> | undefined;
    let terminalCompletion: Promise<void> | null = null;
    let resolveTerminal: (() => void) | null = null;
    let estimates: ReturnType<typeof setInterval> | undefined;
    let samples: ReturnType<typeof setInterval> | undefined;
    const finishTerminal = () => {
      terminalDone = true;
      clearTimeout(terminalTimer);
      clearTimeout(terminalDeadline);
      resolveTerminal?.();
      resolveTerminal = null;
    };
    const estimateNow = async () => {
      if (disposed || arrival.current || estimateBusy || document.hidden || !freshFix(latest.current)) return;
      estimateBusy = true;
      try {
        const next = await api.trafficEstimate(
          route.id,
          latest.current,
          controller.signal,
        );
        if (!disposed) {
          setEstimate(next);
          setError("");
        }
      } catch {
        if (!disposed) {
          setEstimate(null);
          setError("עדכון התנועה אינו זמין כרגע");
        }
      } finally {
        estimateBusy = false;
      }
    };
    const sampleNow = async () => {
      if (
        disposed ||
        terminalDone ||
        !share ||
        sampleBusy ||
        document.hidden
      )
        return;
      const point = terminalQueued ? terminalFix : latest.current;
      if (!freshFix(point, Date.now(), 50) || (point as LocationFix & { mock?: boolean }).mock === true) return;
      if (point.observedAt <= lastAttempt.current.observedAt) {
        // This arrival fix was already sent by a regular tick. Do not replay it.
        if (terminalQueued) finishTerminal();
        return;
      }
      // The server throttles per installation, including when the route changes.
      const wait = lastAttempt.current.at + 5100 - Date.now();
      if (wait > 0) {
        if (terminalQueued) {
          clearTimeout(terminalTimer);
          terminalTimer = setTimeout(() => void sampleNow(), wait);
        }
        return;
      }
      sampleBusy = true;
      const terminalRequest = terminalQueued;
      lastAttempt.current = { at: Date.now(), observedAt: point.observedAt };
      try {
        await api.trafficSample(route.id, point, controller.signal);
      } catch {
      } finally {
        sampleBusy = false;
        if (terminalRequest) {
          finishTerminal();
        } else if (terminalQueued && !disposed) {
          // Arrival may occur while the preceding regular sample is in flight.
          void sampleNow();
        }
      }
    };
    const queueArrival = () => {
      if (!arrival.current || disposed || terminalDone) return;
      terminalQueued = true;
      clearInterval(estimates);
      clearInterval(samples);
      if (freshFix(latest.current, Date.now(), 50)) terminalFix = latest.current;
      void sampleNow();
    };
    flushTerminal.current = point => {
      if (!share || disposed || terminalDone || !freshFix(point, Date.now(), 50) ||
          (point as LocationFix & { mock?: boolean }).mock === true) return Promise.resolve();
      terminalQueued = true;
      terminalFix = point;
      clearInterval(estimates);
      clearInterval(samples);
      if (!terminalCompletion) {
        terminalCompletion = new Promise<void>(resolve => { resolveTerminal = resolve; });
        // An unavailable network must never prevent the car trip from ending.
        terminalDeadline = setTimeout(finishTerminal, 10_000);
      }
      void sampleNow();
      return terminalCompletion;
    };
    notifyArrival.current = queueArrival;
    document.addEventListener("visibilitychange", queueArrival);
    if (arrival.current) queueArrival();
    else {
      void estimateNow();
      void sampleNow();
      estimates = setInterval(estimateNow, 15_000);
      samples = setInterval(sampleNow, 6000);
    }
    return () => {
      disposed = true;
      notifyArrival.current = null;
      flushTerminal.current = null;
      controller.abort();
      clearInterval(estimates);
      clearInterval(samples);
      clearTimeout(terminalTimer);
      finishTerminal();
      document.removeEventListener("visibilitychange", queueArrival);
    };
  }, [route?.id, active, share]);
  // Arrival must not recreate/abort the sampling effect before its last fix is sent.
  useEffect(() => {
    if (arrived) notifyArrival.current?.();
  }, [arrived, fix?.observedAt]);
  const fresh =
    estimate && Date.now() - estimate.updatedAt <= 45_000 ? estimate : null;
  return { estimate: fresh, error, flushArrival: (point: LocationFix) => flushTerminal.current?.(point) ?? Promise.resolve() };
}
